import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { DIVISION_COLOR } from "@/lib/constants";

// Non-recursive tree renderer — flatten with { node, depth, ancestorsExpanded }
// and render as a flat list. Avoids Babel maximum-call-stack error with recursive JSX.
export default function OrgTree({ roots, query, canManage, onSelect, viewerId, canSeeAllLevels = true }) {
  const [expanded, setExpanded] = useState(() => new Set()); // set of node ids that are collapsed=false by default

  // Compute default expanded set: root + first depth by default
  const defaultExpanded = useMemo(() => {
    const s = new Set();
    const walk = (nodes, depth) => {
      nodes.forEach((n) => {
        if (depth < 2) s.add(n.id);
        if (n.children && n.children.length) walk(n.children, depth + 1);
      });
    };
    walk(roots, 0);
    return s;
  }, [roots]);

  const isExpanded = (id) => (expanded.has(id) ? true : (defaultExpanded.has(id) && !expanded.has("collapsed-" + id)));

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (isExpanded(id)) {
        next.delete(id);
        next.add("collapsed-" + id);
      } else {
        next.delete("collapsed-" + id);
        next.add(id);
      }
      return next;
    });
  };

  // Flatten tree honoring collapsed nodes
  const flatList = useMemo(() => {
    const out = [];
    const stack = [];
    roots.forEach((r) => stack.push({ node: r, depth: 0 }));
    // Iterative DFS with stack that respects order
    const visit = (nodes, depth) => {
      nodes.forEach((n) => {
        out.push({ node: n, depth });
        if (n.children && n.children.length && isExpanded(n.id)) {
          visit(n.children, depth + 1);
        }
      });
    };
    visit(roots, 0);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roots, expanded]);

  const q = (query || "").toLowerCase();

  return (
    <div className="space-y-1">
      {flatList.map(({ node, depth }) => {
        const hasKids = node.children && node.children.length > 0;
        const matches = !q || (node.name || "").toLowerCase().includes(q) || (node.role_title || "").toLowerCase().includes(q);
        const opened = isExpanded(node.id);
        const rowCls = "flex items-stretch border bg-white " + (matches ? "border-slate-200" : "border-slate-100 opacity-60");
        const chipRotate = opened ? "rotate-0" : "-rotate-90";
        const avatarBg = node.division === "BOD" ? "bg-[#FF5A1F]" : "bg-[#0B1D3A]";
        const personCls = "flex-1 flex items-center gap-3 px-3 py-2 text-left " + (canManage ? "hover:bg-slate-50" : "cursor-default");
        const initials = node.name.split(" ").slice(0, 2).map((n) => n[0]).join("");
        const subLabel = node.division === "Project" && node.sub_division ? " - " + node.sub_division : "";
        return (
          <div key={node.id} style={{ marginLeft: depth * 24 }} data-testid={"org-node-" + node.id}>
            <div className={rowCls}>
              {hasKids ? (
                <button
                  type="button"
                  data-testid={"org-toggle-" + node.id}
                  onClick={() => toggle(node.id)}
                  className="w-8 flex items-center justify-center bg-slate-50 hover:bg-[#0B1D3A] hover:text-white border-r border-slate-200 transition-colors"
                  title={opened ? "Collapse" : "Expand"}
                >
                  <ChevronDown size={14} className={"transition-transform " + chipRotate} />
                </button>
              ) : null}
              <button
                type="button"
                disabled={!canManage}
                data-testid={"org-person-" + node.id}
                onClick={() => { if (canManage) onSelect(node.id); }}
                className={personCls}
              >
                <div className={"w-8 h-8 flex items-center justify-center font-display font-semibold text-xs text-white " + avatarBg}>{initials}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#0B1D3A] truncate">{node.name}</div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {node.role_title ? <span className="font-semibold text-slate-600">{node.role_title}</span> : <span className="italic text-slate-400">Belum ada jabatan</span>}
                    {subLabel}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-0.5 shrink-0">
                  <span className={"px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider " + DIVISION_COLOR[node.division]}>{node.division}</span>
                  {(canSeeAllLevels || node.id === viewerId) && (
                    <span className="px-1.5 py-0.5 text-[9px] bg-slate-100 font-semibold uppercase tracking-wider">Lv{node.level}</span>
                  )}
                </div>
                {hasKids ? (<span className="ml-2 text-[10px] text-slate-400 tabular-nums shrink-0">{"↓ " + node.children.length}</span>) : null}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
